import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import { jsonClass, jsonProperty } from '@sumbricht/json-class-serializer'
import { BoVisitor } from './bo-visitor.js'
import { BoReference } from './bo-reference.js'
import { EntityProperty } from './entity-property.js'
import { RelationType as TypeormRelationType } from 'typeorm/metadata/types/RelationTypes.js'

export const EntityRelationTypeNames = {
	'one-to-many':
		'One instance of this Entity has a relationship to multiple instances of target Entity (e.g. Person ➔ Address)',
	'many-to-one':
		'Many instances of this Entity have a relationship to a single instance of the target Entity (e.g. Address ➔ Person)',
	'one-to-one-owning':
		'This Entity and target Entity have a one-to-one relationship (e.g. Car ⬌ Engine). This Entity owns the relationship (ID property)',
	'one-to-one-secondary':
		'This Entity and target Entity have a one-to-one relationship (e.g. Car ⬌ Engine). The other Entity owns the relationship',
} as const
export type RelationType = keyof typeof EntityRelationTypeNames
export const TypeormRelationTypes: Record<RelationType, TypeormRelationType> = {
	'one-to-many': 'one-to-many',
	'many-to-one': 'many-to-one',
	'one-to-one-owning': 'one-to-one',
	'one-to-one-secondary': 'one-to-one',
}

@jsonClass('EntityRelation')
export class EntityRelation {
	@jsonProperty(String) uuid!: string
	@jsonProperty(String) name!: string
	@jsonProperty(String) idProperty: string = ''
	@jsonProperty(String) type: RelationType = 'many-to-one'
	@jsonProperty(BoReference) targetEntityRef!: BoReference
	@jsonProperty(String) inverseRelation: string = ''
	@jsonProperty(Boolean) enforceConstraint: boolean = true
	@jsonProperty(Boolean) cascadeOnDelete: boolean = false

	constructor(init?: Partial<EntityRelation>) {
		assignCommonProperties(this, init)
	}

	getIdPropertyDefinition() {
		if (!this.hasInverseRelation()) {
			return new EntityProperty({
				uuid: this.uuid,
				name: this.getIdPropertyName(),
				type: this.targetEntityRef
					? `${this.targetEntityRef.getQualifiedName()}.Id`
					: 'never',
				defaultExpression: 'undefined',
			})
		}

		return null
	}

	getPropertyDefinitions() {
		const type = this.targetEntityRef
			? this.type == 'one-to-many'
				? `${this.targetEntityRef.getQualifiedName()}[]`
				: `${this.targetEntityRef.getQualifiedName()}`
			: 'never'

		return [
			new EntityProperty({
				name: this.name,
				type,
			}),
			this.getIdPropertyDefinition(),
		].filter(Boolean) as EntityProperty[]
	}

	getIdPropertyName() {
		return this.idProperty || `${this.name}Id`
	}

	hasInverseRelation() {
		return this.type == 'one-to-many' || this.type == 'one-to-one-secondary'
	}

	hasIdProperty() {
		return !this.hasInverseRelation()
	}

	visit(visitor: BoVisitor, pathPrefix: (string | number)[]): void {
		visitor.visitBoReference(this.targetEntityRef, [
			...pathPrefix,
			'targetEntityRef',
		])
	}
}
